import ErrorBoundary from 'js/academia_components/ErrorBoundary';
import React, { ComponentType } from 'react';
// eslint import can't figure out react-on-rails' weird module resolution
// eslint-disable-next-line import/no-unresolved
import ReactOnRails, { RailsContext } from 'react-on-rails/client';

import { AutoPropsProvider } from 'js/auto_props/react/AutoPropsProvider';
import { AutoPropsMap } from 'js/auto_props/type_map';

type CustomRailsContext = RailsContext & {
  autoProps?: AutoPropsMap;
};

type GeneratorFn = (
  props: object,
  railsContext: CustomRailsContext,
) => JSX.Element;

function isGeneratorFn(
  component: ComponentType | GeneratorFn,
): component is GeneratorFn {
  return component.length === 2;
}

/**
 * Wraps a component or generator function with an ErrorBoundary.
 * This is a low effort minimal error boundary that ideally should never be directly triggered.
 * Somewhere higher in the component tree should be a a nicer error boundary for your specific
 * page or component.
 */
const addErrorBoundary = (CompOrGen: ComponentType | GeneratorFn) => {
  /* eslint-disable react/display-name */
  const generator: GeneratorFn = isGeneratorFn(CompOrGen)
    ? CompOrGen
    : (props: object, _railsContext: CustomRailsContext) => (
        <CompOrGen {...props} />
      );

  return (props: object, railsContext: CustomRailsContext) => () =>
    typeof railsContext.autoProps === 'object' ? (
      <ErrorBoundary>
        <AutoPropsProvider props={railsContext.autoProps}>
          {generator(props, railsContext)}
        </AutoPropsProvider>
      </ErrorBoundary>
    ) : (
      <ErrorBoundary>{generator(props, railsContext)}</ErrorBoundary>
    );
  /* eslint-enable react/display-name */
};

type Components = Record<string, ComponentType<any> | GeneratorFn>;

/**
 * Register React components for use via Rails.
 * Components are registered under their key on the `components` object.
 * You can enable hot reloading by passing an array of dependencies
 * as the second argument.
 *
 * ## Basic Usage
 *
 * @example
 * ```js
 * registerForRails({
 *   InterruptionChooser, // JS object shorthand, registers as 'InterruptionChooser'
 *   AuthorPortal: AuthorPortal, // registers as 'AuthorPortal'
 *   DifferentName: SeasonalDiscountUnibrow // registers as 'DifferentName'
 * })
 * ```
 *
 * ## Hot Reloading
 *
 * @example
 * ```js
 * registerForRails({
 *   AdminUsersPage
 * }, ['js/admin_content/users']) // reload when anything in this dir changes
 * ```
 */
const registerForRails = (
  /**
   * Object of components or React on Rails generator fns.
   * Each component's key will be the name it's registered under.
   */
  components: Components,
  /**
   * (Currently unused)
   * List of files/directories to watch for updates.
   * The registered components will rerender if any files change.
   * Without this, your component won't hot-reload.
   */
  _dependencies?: string[],
): void => {
  const componentsWithErrors: any = {};
  for (const key in components) {
    componentsWithErrors[key] = addErrorBoundary(components[key]);
  }

  ReactOnRails.register(componentsWithErrors);
};

export default registerForRails;
